import { NavLink } from 'react-router';
import { BellRing, LayoutDashboard, Network, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils.ts';

/**
 * BottomNav — mobile-only fixed-bottom destination strip.
 *
 * Per spec D-7 / addendum Q-1 default: four destinations.
 *   Home → /home
 *   Network → /network (the unified inventory, Q-A6)
 *   Alerts → /incidents (oncall-critical landing per spec §4)
 *   Account → /account/profile
 *
 * "Alerts" maps to /incidents (the oncall list), not /alerts (the
 * alert-rule editor). Phone-side users want to scan/ack incidents, not
 * edit alert rules. The naming follows the bottom-nav convention from
 * the mockups.
 *
 * Hidden at md+ (the persistent drawer covers navigation there).
 *
 * Each item is min-h-11 per spec §5 touch-target rules. Safe-area
 * padding on the bottom for iOS home-indicator clearance.
 */

interface BottomNavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const ITEMS: readonly BottomNavItem[] = [
  { to: '/home', label: 'Home', icon: LayoutDashboard, end: true },
  { to: '/network', label: 'Network', icon: Network },
  { to: '/incidents', label: 'Alerts', icon: BellRing },
  { to: '/account/profile', label: 'Account', icon: UserRound },
];

export function BottomNav() {
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 flex h-14 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] px-[env(safe-area-inset-left)]"
      style={{ paddingRight: 'env(safe-area-inset-right)' }}
      aria-label="Quick navigation"
    >
      {ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'flex-1 inline-flex flex-col items-center justify-center gap-0.5 min-h-11 text-[11px] font-medium transition-colors',
              isActive
                ? 'text-brand'
                : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          <item.icon className="w-5 h-5" aria-hidden="true" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
